import { useQuery } from '@apollo/client';
import { useParams, useSearchParams } from 'react-router-dom';

import TitleBar from '../../../components/TitleBar';
import { Footer } from '../../../components/common/Footer';
import { Field } from '../../../components/generic/Field';
import { SUPPLIER_DEPENDENCIES_SUMMARY } from '../queries';

import { Table } from '../../../components/generic/TableV2';
import { FETCH_LOCATION_BY_ID } from '../../dataInputs/queries/locations';
import TableV3 from '../../../components/generic/Table/TableV3';
import { ColumnsType } from 'antd/es/table';
import { tableSortingMethods } from '../../../utils';
import FinancialData from '../../../components/common/FinancialData';
import { camelCaseToDash } from '../../../utils/camelCaseToDash';
import { startCase } from 'lodash';
import QueryErrorBoundary from '../../../components/error-handling';
import { ExportColumn } from '../../../interfaces/ExportColumn';
import { baseColumnsSummary } from './exportColumns';
import TitleUpdater from '../../../components/common/TitleUpdater';

const SupplierDependenciesSummary = () => {
  const params = useParams();

  // const location = useQuery(FETCH_LOCATION_BY_ID, {
  //   variables: { uuid: params.locationId! },
  // });

  const [searchParams] = useSearchParams();
  const locationName = searchParams.get('locationName') ?? '';

  const { data, loading, error } = useQuery(SUPPLIER_DEPENDENCIES_SUMMARY, {
    variables: { locationUuid: params.locationId },
  });

  const columns: ColumnsType<any> = [
    {
      title: 'Supplier',
      dataIndex: 'supplierName',
      key: 'supplierName',
      align: 'left',
      width: 200,
      sorter: (a, b) =>
        tableSortingMethods.string(a.supplierName, b.supplierName),
      render: (text: string) => (
        <span className="custom-table--column">
          {startCase(text.toLowerCase())}
        </span>
      ),
    },
    {
      title: 'Revenue Supported  Annually',
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(a.totalRevenue, b.totalRevenue),
      render: (text: number) => (
        <span className="custom-table--column custom-table--column-revenue">
          <FinancialData text={text} />
        </span>
      ),
    },
    {
      title: '% Of Location Supported',
      dataIndex: 'percentageOfLocationSupported',
      key: 'percentageOfLocationSupported',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.percentageOfLocationSupported,
          b.percentageOfLocationSupported,
        ),
      render: (text: number) => (
        <span className="custom-table--column ">{text.toFixed(0) + '%'}</span>
      ),
    },
    {
      title: 'Sourcing Status',
      dataIndex: 'sourcingStatus',
      key: 'sourcingStatus',
      align: 'left',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.string(a.sourcingStatus, b.sourcingStatus),
      render: (text: string) => (
        <span className="custom-table--column">{camelCaseToDash(text)}</span>
      ),
    },
    {
      title: 'BCP Uploaded in MEO',
      dataIndex: 'bcpUploaded',
      key: 'bcpUploaded',
      align: 'center',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.boolean(a.bcpUploaded, b.bcpUploaded),
      render: (text: string) => (
        <span className="custom-table--column">{text ? 'Yes' : 'No'}</span>
      ),
    },
    {
      title: 'Supplier BCP Date',
      dataIndex: 'bcpUpdatedAt',
      key: 'bcpUpdatedAt',
      align: 'left',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.date(a.bcpUpdatedAt, b.bcpUpdatedAt),
      render: (text: string) => (
        <span className="custom-table--column">
          {text ? (
            text?.slice(0, 10)
          ) : (
            <span className="placeholder-text">No available date</span>
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <TitleUpdater title={`${locationName}/Supplier Summary`} />
      <TitleBar
        title="Dependency Mapping"
        subtitle={`${locationName} // Supplier Summary`}
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          title="Supplier Dependency Summary"
          columns={columns}
          data={data?.findAll}
          loading={loading}
          customStyles="custom-table--dependency-mapping"
          exportExcelColumns={baseColumnsSummary}
        />
      </QueryErrorBoundary>
      <Footer
        backPath={`/suppliers-dependencies/${params.locationId}/mapping?locationName=${locationName}`}
        nextPath={`/suppliers-dependencies/`}
        goNextText="Location Selection"
      />
    </>
  );
};

export default SupplierDependenciesSummary;
